import axios from 'axios';
import { getIsApiPending, getPayloadCollection, getPayloadData } from '../../../selectors/travelSs/payloadCollection';
import {
    apiTransitionPayloadCollection,
    submitPayloadDataSuccess,
    submitPayloadDataFailure,
} from '../../../actions/travelSs/payloadCollection';
import {
    getAccessToken,
    getPaymentMode,
    getPaymentsForm,
    getPaymentStatusData,
    getStatusApiCallStatus,
    getStkData,
} from '../../../selectors/travelSs/payments';
import { getCountries, getPremiumCalculation, getQuoteForm, getStep } from '../../../selectors/travelSs/quote';
import { getAgentForm } from '../../../selectors/travelSs/travelAgentModal';
import { getCustomer, getTravelDetailsForm, getTravellers } from '../../../selectors/travelSs/travelDetails';
import { getNextOfKinForm } from '../../../selectors/travelSs/nextOfKin';
import { triggerDocumentUploadChecks } from '../../../actions/travelSs/travelDetails';
import { apiTransitionPaymentsStatus, triggerPaymentsStkPush } from '../../../actions/travelSs/payments';

export const payloadSubmitService = (store, next, action) => {
    const step = getStep(store.getState());
    const paymentMode = getPaymentMode(store.getState());
    (async () => {
        if (!getIsApiPending(store.getState())) {
            try {
                (async () => {
                    store.dispatch(apiTransitionPayloadCollection.pending());
                })();
                const resp = await makeSubmission(store.getState(), store);

                store.dispatch(submitPayloadDataSuccess(resp.data));
                if (step !== 'payment') {
                    store.dispatch(apiTransitionPayloadCollection.success());
                }
                if (paymentMode === 'mtn' && step === 'payment') {
                    store.dispatch(triggerPaymentsStkPush());
                }
            } catch (e) {
                console.error(e);
                store.dispatch(submitPayloadDataFailure(e));
                store.dispatch(apiTransitionPayloadCollection.failure());
            }
        }
    })();

    return next(action);
};

const formatTravellers = (state) => {
    const travellers = getTravellers(state);

    const formattedTravellers = [];

    for (let i = 0; i < travellers.length; i++) {
        const traveller = travellers[i];

        formattedTravellers.push({
            full_name: traveller.fullName?.value || 'N/A',
            date_of_birth: traveller.dob?.value || 'N/A',
            passport_number: traveller.passportNumber?.value || 'N/A',
            passportDoc: traveller.passportDocumentId?.value || 'N/A',
        });
    }

    return formattedTravellers;
};

const payloadCollection = (state) => {
    const quotation = getQuoteForm(state);
    const agent = getAgentForm(state);
    const travelDetails = getTravelDetailsForm(state);
    const customer = getCustomer(state);
    const nextOfKin = getNextOfKinForm(state);
    const paymentMode = getPaymentMode(state);
    const paymentsData = getPaymentStatusData(state);
    const payloadCollection = getPayloadData(state);
    const payloadCollectionUUID = getPayloadCollection(state).uuid;
    const payment = getPaymentsForm(state);
    const step = getStep(state);
    const premiumCalculation = getPremiumCalculation(state);
    const countries = getCountries(state);

    const stkData = getStkData(state);

    const determineAxaDestinationArea = (quotation) => {
        if (quotation.selectedCover?.value === 'Schengen') {
            return 'Schengen Area';
        }

        if (quotation.selectedCover?.value === 'East Africa') {
            return 'East African country';
        }

        if (quotation.selectedCover?.value === 'Holiday') {
            return 'Holiday';
        }

        return 'Senior';
    };

    const determineComposition = (quotation) => {
        const adultTravellers = parseInt(quotation.noOfTravellers?.value) || 0;
        const minorTravellers = parseInt(quotation.noOfMinors?.value) || 0;
        const totalNoOfTravellers = adultTravellers + minorTravellers;

        if (totalNoOfTravellers > 1) {
            return 'group';
        }

        return 'single';
    };

    const getNumberOfChildren = (quotation) => {
        if (quotation.selectedCover?.value === 'Senior') {
            return 0;
        }

        return quotation.noOfMinors?.value ? parseInt(quotation.noOfMinors?.value) : 0;
    };

    function formatDate(arr) {
        if (arr.length !== 3) {
            throw new Error("Array must have exactly 3 elements: ['DD', 'MM', 'YYYY']");
        }

        const [day, month, year] = arr;
        return `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
    }

    const quotePayload = {
        full_name: quotation.fullName?.value || 'N/A',
        destination_country: quotation.destinationCountry?.value || 'N/A',
        departure_country: 'South Sudan' || 'N/A',
        start_date: formatDate(quotation.departureDate?.value.split('/')) || 'N/A',
        end_date: formatDate(quotation.returnDate?.value.split('/')) || 'N/A',
        composition: determineComposition(quotation),
        adult: parseInt(quotation.noOfTravellers?.value),
        destination_area: determineAxaDestinationArea(quotation),
        reffererCode: agent.agentCode.value || 'N/A',
        reffererType: agent.intermediaryType.value || 'N/A',
    };

    const travellerDetailsPayload = {
        full_name: quotation.fullName?.value || 'N/A',
        destination_country: quotation.destinationCountry?.value || 'N/A',
        departure_country: 'South Sudan' || 'N/A',
        start_date: formatDate(quotation.departureDate?.value.split('/')) || 'N/A',
        end_date: formatDate(quotation.returnDate?.value.split('/')) || 'N/A',
        composition: determineComposition(quotation),
        adult: quotation.selectedCover?.value !== 'Senior' ? parseInt(quotation.noOfTravellers?.value) : 0,
        destination_area: determineAxaDestinationArea(quotation),
        reffererCode: agent.agentCode.value || 'N/A',
        reffererType: agent.intermediaryType.value || 'N/A',
        nationality: customer.nationalityType?.value || 'N/A',
        passport_number: customer.passportNumber?.value || 'N/A',
        id_number: customer.idNumber?.value || 'N/A',
        occupation: customer.profession?.value || 'N/A',
        phone_number: customer.phoneNumber?.value || 'N/A',
        office_number: customer.officeNumber?.value || 'N/A',
        email_address: customer.email?.value || 'N/A',
        date_of_birth: customer.dob?.value ? formatDate(customer.dob?.value.split('/')) : 'N/A',
        nationalIdDoc: customer.idUpload?.value?.base64File || 'N/A',
        passportDoc: customer.passportBiodata?.value?.base64File || 'N/A',
        town: customer.town?.value || 'N/A',
        postal_home_address: customer.postalAddress?.value || 'N/A',
        travelers: formatTravellers(state),
        emergency_contact: {
            full_name: travelDetails.emergencyContactFullName?.value || 'N/A',
            email_address: travelDetails.emergencyContactEmailAddress?.value || 'N/A',
            phone_number: travelDetails.emergencyContactPhoneNumber?.value || 'N/A',
        },
        how_did_you_hear_about_us: travelDetails.channelWays?.value || 'N/A',
        uuid: payloadCollectionUUID || 'N/A',
    };

    const nextOfKinPayload = {
        full_name: quotation.fullName?.value || 'N/A',
        destination_country: quotation.destinationCountry?.value || 'N/A',
        departure_country: 'South Sudan' || 'N/A',
        start_date: formatDate(quotation.departureDate?.value.split('/')) || 'N/A',
        end_date: formatDate(quotation.returnDate?.value.split('/')) || 'N/A',
        composition: determineComposition(quotation),
        adult: quotation.selectedCover?.value !== 'Senior' ? parseInt(quotation.noOfTravellers?.value) : 0,
        destination_area: determineAxaDestinationArea(quotation),
        reffererCode: agent.agentCode.value || 'N/A',
        reffererType: agent.intermediaryType.value || 'N/A',
        nationality: customer.nationalityType?.value || 'N/A',
        passport_number: customer.passportNumber?.value || 'N/A',
        id_number: customer.idNumber?.value || 'N/A',
        occupation: customer.profession?.value || 'N/A',
        phone_number: customer.phoneNumber?.value || 'N/A',
        office_number: customer.officeNumber?.value || 'N/A',
        email_address: customer.email?.value || 'N/A',
        date_of_birth: customer.dob?.value ? formatDate(customer.dob?.value.split('/')) : 'N/A',
        nationalIdDoc: customer.idUpload?.value?.base64File || 'N/A',
        passportDoc: customer.passportBiodata?.value?.base64File || 'N/A',
        town: customer.town?.value || 'N/A',
        postal_home_address: customer.postalAddress?.value || 'N/A',
        travelers: formatTravellers(state),
        emergency_contact: {
            full_name: travelDetails.emergencyContactFullName?.value || 'N/A',
            email_address: travelDetails.emergencyContactEmailAddress?.value || 'N/A',
            phone_number: travelDetails.emergencyContactPhoneNumber?.value || 'N/A',
        },
        how_did_you_hear_about_us: travelDetails.channelWays?.value || 'N/A',
        uuid: payloadCollectionUUID || 'N/A',
        next_of_kin_full_name: nextOfKin.firstName?.value ? nextOfKin.firstName?.value : 'N/A',
        next_of_kin_phone_number: nextOfKin.phoneNumber?.value || 'N/A',
        next_of_kin_email_address: nextOfKin.email?.value || 'N/A',
        next_of_kin_postal_home_address: nextOfKin.postalAddress?.value || 'N/A',
        next_of_kin_town: nextOfKin.town?.value || 'N/A',
    };

    const paymentPayload = {
        full_name: quotation.fullName?.value || 'N/A',
        destination_country: quotation.destinationCountry?.value || 'N/A',
        departure_country: 'South Sudan' || 'N/A',
        start_date: formatDate(quotation.departureDate?.value.split('/')) || 'N/A',
        end_date: formatDate(quotation.returnDate?.value.split('/')) || 'N/A',
        composition: determineComposition(quotation),
        adult: quotation.selectedCover?.value !== 'Senior' ? parseInt(quotation.noOfTravellers?.value) : 0,
        destination_area: determineAxaDestinationArea(quotation),
        reffererCode: agent.agentCode.value || 'N/A',
        reffererType: agent.intermediaryType.value || 'N/A',
        nationality: customer.nationalityType?.value || 'N/A',
        passport_number: customer.passportNumber?.value || 'N/A',
        id_number: customer.idNumber?.value || 'N/A',
        occupation: customer.profession?.value || 'N/A',
        phone_number: customer.phoneNumber?.value || 'N/A',
        office_number: customer.officeNumber?.value || 'N/A',
        email_address: customer.email?.value || 'N/A',
        date_of_birth: customer.dob?.value ? formatDate(customer.dob?.value.split('/')) : 'N/A',
        nationalIdDoc: customer.idUpload?.value?.base64File || 'N/A',
        passportDoc: customer.passportBiodata?.value?.base64File || 'N/A',
        town: customer.town?.value || 'N/A',
        postal_home_address: customer.postalAddress?.value || 'N/A',
        travelers: formatTravellers(state),
        emergency_contact: {
            full_name: travelDetails.emergencyContactFullName?.value || 'N/A',
            email_address: travelDetails.emergencyContactEmailAddress?.value || 'N/A',
            phone_number: travelDetails.emergencyContactPhoneNumber?.value || 'N/A',
        },
        how_did_you_hear_about_us: travelDetails.channelWays?.value || 'N/A',
        uuid: payloadCollectionUUID || 'N/A',
        next_of_kin_full_name: nextOfKin.firstName?.value ? nextOfKin.firstName?.value : 'N/A',
        next_of_kin_phone_number: nextOfKin.phoneNumber?.value || 'N/A',
        next_of_kin_email_address: nextOfKin.email?.value || 'N/A',
        next_of_kin_postal_home_address: nextOfKin.postalAddress?.value || 'N/A',
        next_of_kin_town: nextOfKin.town?.value || 'N/A',
        mode_of_payment: paymentMode === 'mtn' ? 'MTNMOMO' : 'BANK',
        momo_phoneNumber: '211' + payment.phoneNumber?.value?.substring(1),
    };

    const payload =
        step === 'quote'
            ? quotePayload
            : step === 'travel'
            ? travellerDetailsPayload
            : step === 'kin'
            ? nextOfKinPayload
            : paymentPayload;

    return {
        ...payload,
    };
};

const makeSubmission = async (state, store) => {
    const loginData = getAccessToken(state);

    try {
        const url = '/om-api/travel-ss/onboarding';
        return await axios.post(url, payloadCollection(state), {
            headers: {
                'Content-Type': 'application/json',
                accessToken: loginData.accessToken,
            },
        });
    } catch (e) {
        throw e;
    }
};
